/**
 * Bestimmt die Zielroute nach erfolgreichem Login basierend auf Rolle
 * und gewähltem Workspace. Pure Funktion ohne React-Abhängigkeit, damit
 * sie unabhängig getestet werden kann.
 *
 * Drei voneinander getrennte Login-URLs:
 *   `/login/admin`     — nur für Plattform-Admins (Icarus)
 *   `/login/klinik`    — für Klinik-Personal (org_admin, therapeut, assistenz)
 *   `/login/ambulant`  — für Praxis-Personal (org_admin, therapeut, assistenz)
 *
 * Wenn der eingeloggte User nicht zur gewählten Workspace passt, gibt
 * der Validator einen `mismatch`-Status zurück — der Caller logged dann
 * aus und zeigt eine Fehlermeldung, statt den User in einen Workspace
 * zu lassen, in dem er nichts sehen würde.
 */

import type { Role } from './types';

export const PLATFORM_ADMIN_HOME = '/admin';
export const APP_HOME = '/dashboard';

export type Workspace = 'admin' | 'klinik' | 'ambulant' | 'patient';

/**
 * Die nach erfolgreichem Login anzusteuernde URL — wenn keine Workspace-
 * Wahl vorliegt (Legacy-Pfad), wird sie aus der Rolle abgeleitet, damit
 * existierende Deep-Links auf `/login` weiterhin funktionieren.
 */
export function getPostLoginRoute(role: Role | null | undefined): string {
  if (role === 'admin') return PLATFORM_ADMIN_HOME;
  return APP_HOME;
}

/**
 * Prüft, ob die Profil-Rolle zum gewählten Workspace passt.
 *
 *   • Admin-Workspace verlangt `role='admin'` (Plattform-Admin).
 *   • Klinik- und Ambulant-Workspace akzeptieren beide `org_admin` /
 *     `therapeut` / `assistenz` — genau wie schon vor der Rollen-
 *     Zusammenlegung, denn diese Funktion ist rein rollenbasiert. Die
 *     Org-`setting_type`-Kompatibilität (praxis vs. klinik) wird separat
 *     in der App per `<RoleGuard>`/Scope geprüft (ADR-0007) und war für
 *     `therapeut`/`assistenz` schon immer nur dort, nicht hier, geregelt.
 *
 * Patient-Login läuft über `supabasePortal` und hat einen eigenen
 * Validierungspfad — deswegen `'patient'` hier nicht gelistet.
 */
export type WorkspaceMatch =
  | { ok: true; route: string }
  | { ok: false; reason: 'wrong_workspace' | 'no_profile' };

export function validateWorkspaceMatch(
  workspace: Workspace,
  role: Role | null | undefined,
): WorkspaceMatch {
  if (!role) return { ok: false, reason: 'no_profile' };
  if (workspace === 'admin') {
    return role === 'admin'
      ? { ok: true, route: PLATFORM_ADMIN_HOME }
      : { ok: false, reason: 'wrong_workspace' };
  }
  if (workspace === 'klinik' || workspace === 'ambulant') {
    return role === 'org_admin' || role === 'therapeut' || role === 'assistenz'
      ? { ok: true, route: APP_HOME }
      : { ok: false, reason: 'wrong_workspace' };
  }
  // 'patient' nutzt einen anderen Auth-Backend (supabasePortal).
  return { ok: false, reason: 'wrong_workspace' };
}

/**
 * Mappt URL-Pfad → Workspace. Nutzt expliziten Lookup statt regex, damit
 * Fehlangriffe (z. B. `/login/admin/foo`) sicher ausgeschlossen sind.
 */
export function workspaceFromPath(pathname: string): Workspace | null {
  if (pathname === '/login/admin') return 'admin';
  if (pathname === '/login/klinik') return 'klinik';
  if (pathname === '/login/ambulant') return 'ambulant';
  if (pathname === '/login/patient') return 'patient';
  return null;
}

/**
 * Routes, die der Plattform-Admin sehen darf. Workflow-Routen sind durch
 * den `<RoleGuard>` schon abgewehrt; diese Liste ist eine schnelle
 * Vorabprüfung in `<ProtectedRoute>`, damit Deep-Links direkt umgeleitet
 * werden statt erst die "Kein Zugriff"-Seite zu zeigen.
 */
const PLATFORM_ADMIN_PATH_PREFIXES = ['/admin'];

export function isPlatformAdminPath(pathname: string): boolean {
  return PLATFORM_ADMIN_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}
